import React, { useEffect, useState, useMemo } from "react";
import { api, formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Plus, Edit2, Trash2, X, Key, ShieldCheck, ShieldOff, User as UserIcon, Lock, Unlock } from "lucide-react";
import { toast } from "sonner";

// Iteration 30: System roles for access management (distinct from HR position)
const SYSTEM_ROLES = [
  { value: "admin", label: "Суперадміністратор" },
  { value: "director", label: "Директор" },
  { value: "accountant", label: "Бухгалтер (перегляд фінансів)" },
  { value: "manager", label: "Менеджер клініки" },
  { value: "registrar", label: "Реєстратор" },
  { value: "doctor", label: "Лікар" },
  { value: "surgeon", label: "Хірург" },
  { value: "salesperson", label: "Фахівець оптики" },
  { value: "paramedic", label: "Фельдшер" },
  { value: "optometrist", label: "Оптометрист" },
];

const EMPTY = {
  full_name: "", phone: "", position: "", role: "doctor", is_active: true,
  percent_rate: 0, fixed_rate: 0, comment: "", email: "", password: "",
  groups: ["clinic"],
  can_operate: false, is_external_surgeon: false,
};

export default function Employees() {
  const [tab, setTab] = useState("clinic");
  const [items, setItems] = useState([]);
  const [roles, setRoles] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [me, setMe] = useState(null);   // Iteration 30

  const load = async () => {
    try {
      const [e, r, u] = await Promise.all([api.get("/employees"), api.get("/auth/roles"), api.get("/auth/me")]);
      setItems(e.data);
      setRoles(r.data);
      setMe(u.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  useEffect(() => { load(); }, []);

  const filteredEmployees = useMemo(() => {
    return items.filter((it) => (it.groups || ["clinic"]).includes(tab));
  }, [items, tab]);

  const startCreate = () => {
    setEditing(null);
    setForm({ ...EMPTY, groups: tab === "trip" ? ["trip"] : ["clinic"] });
    setOpen(true);
  };
  const startEdit = (it) => {
    setEditing(it);
    setForm({
      ...EMPTY,
      ...it,
      groups: it.groups && it.groups.length ? it.groups : ["clinic"],
      // Iteration 34: default can_operate=true for legacy surgeons without the flag.
      can_operate: it.can_operate !== undefined ? !!it.can_operate : it.role === "surgeon",
      is_external_surgeon: !!it.is_external_surgeon,
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.full_name?.trim()) { toast.error("Введіть ПІБ"); return; }
    if (!form.groups?.length) { toast.error("Оберіть хоча б одну групу (Клініка / Відрядження)"); return; }
    try {
      if (editing) {
        await api.put(`/employees/${editing.id}`, form);
        toast.success("Працівника оновлено");
      } else {
        await api.post("/employees", form);
        toast.success("Працівника додано");
      }
      setOpen(false);
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (it) => {
    if (!window.confirm(`Видалити ${it.full_name}?`)) return;
    try {
      await api.delete(`/employees/${it.id}`);
      toast.success("Видалено");
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const toggleGroup = (g) => {
    const cur = new Set(form.groups || []);
    if (cur.has(g)) cur.delete(g); else cur.add(g);
    setForm({ ...form, groups: Array.from(cur) });
  };

  const counts = useMemo(() => ({
    clinic: items.filter((it) => (it.groups || ["clinic"]).includes("clinic")).length,
    trip: items.filter((it) => (it.groups || ["clinic"]).includes("trip")).length,
  }), [items]);

  return (
    <div data-testid="employees-page" className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="heading text-3xl font-bold text-[#0F2027]">Працівники</h1>
          <p className="text-sm text-slate-500">Персонал клініки, бригади відряджень та зовнішні лікарі</p>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-white border border-slate-200">
          <TabsTrigger value="clinic" data-testid="tab-clinic-staff">Персонал клініки <Badge className="ml-2 bg-slate-100 text-slate-600">{counts.clinic}</Badge></TabsTrigger>
          <TabsTrigger value="trip" data-testid="tab-trip-staff">Персонал відрядження <Badge className="ml-2 bg-slate-100 text-slate-600">{counts.trip}</Badge></TabsTrigger>
          <TabsTrigger value="external" data-testid="tab-external-doctors">Зовнішні лікарі</TabsTrigger>
        </TabsList>

        {/* Clinic & Trip — same staff list, filtered */}
        {(tab === "clinic" || tab === "trip") && (
          <TabsContent value={tab} className="mt-4">
            <div className="flex justify-end mb-3">
              <Button onClick={startCreate} data-testid="add-employee" className="bg-[#0F2027] hover:bg-[#0F2027]/90">
                <Plus className="h-4 w-4 mr-2" /> Додати {tab === "trip" ? "у відрядження" : "у клініку"}
              </Button>
            </div>
            <Card className="gv-card">
              <CardContent className="p-0 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider sticky top-0 z-10">
                    <tr>
                      <th className="text-left p-4">ПІБ</th>
                      <th className="text-left p-4">Телефон</th>
                      <th className="text-left p-4">Роль</th>
                      <th className="text-left p-4">Посада</th>
                      <th className="text-left p-4">Групи</th>
                      <th className="text-right p-4">% / Ставка</th>
                      <th className="text-center p-4">Статус</th>
                      <th className="text-right p-4 w-32">Дії</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredEmployees.map((it) => (
                      <tr key={it.id} className="border-t border-slate-100 hover:bg-slate-50/50" data-testid={`emp-row-${it.id}`}>
                        <td className="p-4 font-medium text-[#0F2027]">{it.full_name}</td>
                        <td className="p-4 text-slate-600">{it.phone}</td>
                        <td className="p-4"><Badge variant="secondary" className="bg-teal-50 text-teal-700 hover:bg-teal-50">{it.role_label}</Badge></td>
                        <td className="p-4 text-slate-600">{it.position}</td>
                        <td className="p-4 text-xs">
                          <div className="flex gap-1 flex-wrap">
                            {(it.groups || ["clinic"]).includes("clinic") && <Badge className="bg-sky-50 text-sky-700">Клініка</Badge>}
                            {(it.groups || []).includes("trip") && <Badge className="bg-violet-50 text-violet-700">Відрядження</Badge>}
                          </div>
                        </td>
                        <td className="p-4 text-right tabular-nums">{it.percent_rate}% / {it.fixed_rate}</td>
                        <td className="p-4 text-center">
                          <Badge className={it.is_active ? "bg-emerald-50 text-emerald-700 hover:bg-emerald-50" : "bg-slate-100 text-slate-500 hover:bg-slate-100"}>
                            {it.is_active ? "Активний" : "Неактивний"}
                          </Badge>
                        </td>
                        <td className="p-4 text-right">
                          <div className="flex gap-1 justify-end">
                            <Button size="icon" variant="ghost" onClick={() => startEdit(it)} data-testid={`edit-employee-${it.id}`}><Edit2 className="h-4 w-4" /></Button>
                            <Button size="icon" variant="ghost" onClick={() => remove(it)} data-testid={`delete-employee-${it.id}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {filteredEmployees.length === 0 && (
                      <tr><td colSpan={8} className="p-8 text-center text-slate-400">
                        Немає працівників у групі «{tab === "trip" ? "Персонал відрядження" : "Персонал клініки"}»
                      </td></tr>
                    )}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </TabsContent>
        )}

        <TabsContent value="external" className="mt-4">
          <ExternalDoctorsTab />
        </TabsContent>
      </Tabs>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? "Редагувати працівника" : "Новий працівник"}</DialogTitle>
            <DialogDescription>
              Працівник може належати до однієї або обох груп: Персонал клініки та/або Персонал відрядження.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2"><Label>ПІБ <span className="text-rose-500">*</span></Label>
              <Input data-testid="emp-name" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} /></div>
            <div><Label>Телефон</Label><Input data-testid="emp-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
            <div><Label>Посада</Label><Input value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })} /></div>
            <div><Label>Роль</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="emp-role"><SelectValue /></SelectTrigger>
                <SelectContent>{roles.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-3 mt-7">
              <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} />
              <Label>Активний</Label>
            </div>
            <div className="md:col-span-2 p-3 border border-slate-200 rounded-md bg-slate-50/50">
              <Label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Належить до груп <span className="text-rose-500">*</span></Label>
              <div className="flex flex-col gap-2 mt-2">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    data-testid="emp-group-clinic"
                    checked={(form.groups || []).includes("clinic")}
                    onChange={() => toggleGroup("clinic")}
                    className="accent-[#0D9488] h-4 w-4"
                  />
                  <Badge className="bg-sky-50 text-sky-700">Персонал клініки</Badge>
                  <span className="text-xs text-slate-500">{"— з'являється в блоці «Персонал клініки» рекомендації"}</span>
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    data-testid="emp-group-trip"
                    checked={(form.groups || []).includes("trip")}
                    onChange={() => toggleGroup("trip")}
                    className="accent-[#0D9488] h-4 w-4"
                  />
                  <Badge className="bg-violet-50 text-violet-700">Персонал відрядження</Badge>
                  <span className="text-xs text-slate-500">{"— з'являється в блоці «Бригада направлення»"}</span>
                </label>
              </div>
            </div>
            <div className="md:col-span-2 p-3 border border-slate-200 rounded-md bg-slate-50/50">
              <Label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Операційний список</Label>
              <div className="flex flex-col gap-2 mt-2">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    data-testid="emp-can-operate"
                    checked={!!form.can_operate}
                    onChange={(e) => setForm({ ...form, can_operate: e.target.checked, is_external_surgeon: e.target.checked ? form.is_external_surgeon : false })}
                    className="accent-[#0D9488] h-4 w-4"
                  />
                  <Badge className="bg-teal-50 text-teal-700">Може бути хірургом</Badge>
                  <span className="text-xs text-slate-500">— з'являється у списку хірургів при створенні операційного дня</span>
                </label>
                {form.can_operate && (
                  <label className="flex items-center gap-2 text-sm cursor-pointer ml-6">
                    <input
                      type="checkbox"
                      data-testid="emp-is-external-surgeon"
                      checked={!!form.is_external_surgeon}
                      onChange={(e) => setForm({ ...form, is_external_surgeon: e.target.checked })}
                      className="accent-[#0D9488] h-4 w-4"
                    />
                    <Badge className="bg-slate-100 text-slate-700">Зовнішній хірург</Badge>
                    <span className="text-xs text-slate-500">— доступний тільки для гілки «ФЕК (зовнішні)»</span>
                  </label>
                )}
              </div>
            </div>
            <div><Label>Відсоток нарахування (%)</Label>
              <Input type="number" placeholder="0" value={form.percent_rate ? form.percent_rate : ""} onChange={(e) => setForm({ ...form, percent_rate: e.target.value === "" ? 0 : parseFloat(e.target.value) })} /></div>
            <div><Label>Фіксована ставка</Label>
              <Input type="number" placeholder="0" value={form.fixed_rate ? form.fixed_rate : ""} onChange={(e) => setForm({ ...form, fixed_rate: e.target.value === "" ? 0 : parseFloat(e.target.value) })} /></div>
            <div className="md:col-span-2"><Label>Коментар</Label>
              <Input value={form.comment} onChange={(e) => setForm({ ...form, comment: e.target.value })} /></div>
            {!editing && (
              <>
                <div><Label>Email (для входу, опц.)</Label>
                  <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
                <div><Label>Пароль (опц.)</Label>
                  <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></div>
              </>
            )}
          </div>

          {/* Iteration 30: System access management block — only for existing employees, superadmin only */}
          {editing && me?.role === "admin" && (
            <SystemAccessPanel employee={editing} onChange={load} />
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Скасувати</Button>
            <Button onClick={save} data-testid="save-employee" className="bg-[#0F2027]">Зберегти</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ExternalDoctorsTab() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ full_name: "", phone: "", speciality: "", city: "", clinic: "", comment: "", is_active: true, salary_type: "percent", salary_value: 0 });

  const load = async () => {
    try { const r = await api.get(`/catalog/external-doctors`); setItems(r.data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Помилка"); }
  };
  useEffect(() => { load(); }, []);

  const startCreate = () => { setEditing(null); setForm({ full_name: "", phone: "", speciality: "", city: "", clinic: "", comment: "", is_active: true, salary_type: "percent", salary_value: 0 }); setOpen(true); };
  const startEdit = (it) => { setEditing(it); setForm({ full_name: "", phone: "", speciality: "", city: "", clinic: "", comment: "", is_active: true, salary_type: "percent", salary_value: 0, ...it }); setOpen(true); };
  const save = async () => {
    if (!form.full_name?.trim()) { toast.error("Введіть ПІБ"); return; }
    try {
      if (editing) await api.put(`/catalog/external-doctors/${editing.id}`, form);
      else await api.post("/catalog/external-doctors", form);
      toast.success("Збережено"); setOpen(false); await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const toggle = async (it) => {
    try { await api.put(`/catalog/external-doctors/${it.id}`, { ...it, is_active: !it.is_active }); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async (it) => {
    if (!window.confirm(`Видалити лікаря «${it.full_name}»?`)) return;
    try { await api.delete(`/catalog/external-doctors/${it.id}`); toast.success("Видалено"); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Card className="gv-card">
      <CardContent className="p-0">
        <div className="p-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="font-semibold text-[#0F2027]">Зовнішні лікарі</div>
            <div className="text-xs text-slate-500 mt-1">Лікарі з інших клінік / організацій, що направляють пацієнтів. НЕ є працівниками Global Vision.</div>
          </div>
          <Button onClick={startCreate} size="sm" data-testid="add-ext-doctor" className="bg-[#0F2027]"><Plus className="h-4 w-4 mr-1" />Додати</Button>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-600 sticky top-0 z-10">
            <tr>
              <th className="text-left p-3">ПІБ</th>
              <th className="text-left p-3">Телефон</th>
              <th className="text-left p-3">Спеціальність</th>
              <th className="text-left p-3">Місто</th>
              <th className="text-left p-3">Клініка</th>
              <th className="text-right p-3 bg-amber-50">Налаштування ЗП</th>
              <th className="text-right p-3 bg-emerald-50">Нараховано</th>
              <th className="text-right p-3 bg-sky-50">Виплачено</th>
              <th className="text-right p-3 bg-rose-50">Борг</th>
              <th className="text-center p-3">Стан</th>
              <th className="text-right p-3 w-32">Дії</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it) => (
              <tr key={it.id} className="border-t border-slate-100" data-testid={`ext-doc-row-${it.id}`}>
                <td className="p-3 font-medium">{it.full_name}</td>
                <td className="p-3 text-xs">{it.phone || "—"}</td>
                <td className="p-3 text-xs">{it.speciality || "—"}</td>
                <td className="p-3 text-xs">{it.city || "—"}</td>
                <td className="p-3 text-xs">{it.clinic || "—"}</td>
                <td className="p-3 text-right text-xs bg-amber-50/30 tabular-nums">
                  {it.salary_value > 0 ? (
                    <Badge className={it.salary_type === "fixed" ? "bg-amber-100 text-amber-700" : "bg-sky-100 text-sky-700"}>
                      {it.salary_type === "fixed" ? `${it.salary_value} ₴` : `${it.salary_value}%`}
                    </Badge>
                  ) : <span className="text-slate-400">—</span>}
                </td>
                <td className="p-3 text-right text-xs bg-emerald-50/30 tabular-nums font-semibold text-emerald-700">
                  {it.accrued > 0 ? `${it.accrued.toLocaleString("uk-UA")} ₴` : <span className="text-slate-400">—</span>}
                </td>
                <td className="p-3 text-right text-xs bg-sky-50/30 tabular-nums font-semibold text-sky-700">
                  {it.paid > 0 ? `${it.paid.toLocaleString("uk-UA")} ₴` : <span className="text-slate-400">—</span>}
                </td>
                <td className="p-3 text-right text-xs bg-rose-50/30 tabular-nums font-semibold text-rose-700">
                  {it.debt > 0 ? `${it.debt.toLocaleString("uk-UA")} ₴` : <span className="text-slate-400">0</span>}
                </td>
                <td className="p-3 text-center">{it.is_active ? <Badge className="bg-emerald-50 text-emerald-700">Активний</Badge> : <Badge className="bg-slate-100 text-slate-500">Неактивний</Badge>}</td>
                <td className="p-3 text-right">
                  <div className="flex gap-1 justify-end">
                    <Button size="icon" variant="ghost" onClick={() => startEdit(it)}><Edit2 className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" onClick={() => toggle(it)}>
                      {it.is_active ? <X className="h-4 w-4 text-amber-600" /> : <Plus className="h-4 w-4 text-emerald-600" />}
                    </Button>
                    <Button size="icon" variant="ghost" onClick={() => remove(it)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                  </div>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={11} className="p-8 text-center text-slate-400">Немає зовнішніх лікарів</td></tr>}
          </tbody>
        </table>
      </CardContent>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? "Редагувати зовнішнього лікаря" : "Новий зовнішній лікар"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="md:col-span-2"><Label>ПІБ <span className="text-rose-500">*</span></Label>
              <Input data-testid="ext-doc-name" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} />
            </div>
            <div><Label>Телефон</Label>
              <Input data-testid="ext-doc-phone" value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            </div>
            <div><Label>Спеціальність</Label>
              <Input data-testid="ext-doc-speciality" value={form.speciality || ""} onChange={(e) => setForm({ ...form, speciality: e.target.value })} />
            </div>
            <div><Label>Місто</Label>
              <Input data-testid="ext-doc-city" value={form.city || ""} onChange={(e) => setForm({ ...form, city: e.target.value })} />
            </div>
            <div><Label>Клініка / місце роботи</Label>
              <Input data-testid="ext-doc-clinic" value={form.clinic || ""} onChange={(e) => setForm({ ...form, clinic: e.target.value })} />
            </div>
            <div className="md:col-span-2"><Label>Коментар</Label>
              <Input value={form.comment || ""} onChange={(e) => setForm({ ...form, comment: e.target.value })} />
            </div>
            <div className="md:col-span-2 p-3 border border-slate-200 rounded-md bg-slate-50/50">
              <Label className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2 block">Нарахування зарплати</Label>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Тип нарахування</Label>
                  <Select value={form.salary_type || "percent"} onValueChange={(v) => setForm({ ...form, salary_type: v })}>
                    <SelectTrigger data-testid="ext-doc-salary-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="percent">% від суми операції</SelectItem>
                      <SelectItem value="fixed">Фіксована сума (₴)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">{form.salary_type === "fixed" ? "Сума за операцію (₴)" : "Відсоток (%)"}</Label>
                  <Input
                    type="number"
                    data-testid="ext-doc-salary-value"
                    placeholder="0"
                    value={form.salary_value ? form.salary_value : ""}
                    onChange={(e) => setForm({ ...form, salary_value: e.target.value === "" ? 0 : parseFloat(e.target.value) })}
                  />
                </div>
              </div>
              <div className="text-xs text-slate-500 mt-2">
                Нараховується автоматично, коли операція з рекомендацією, де джерело = «Зовнішній лікар», переходить у статус «Проведено».
              </div>
            </div>
            <label className="md:col-span-2 flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} className="accent-[#0D9488]" />
              Активний
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Скасувати</Button>
            <Button onClick={save} data-testid="ext-doc-save" className="bg-[#0F2027]" disabled={!form.full_name?.trim()}>Зберегти</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}


// ═══════════════════════════════════════════════════════════
// Iteration 30 — System Access Panel (superadmin only)
// ═══════════════════════════════════════════════════════════
function SystemAccessPanel({ employee, onChange }) {
  const [access, setAccess] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ login: "", password: "", role: "doctor", force_password_change: false });
  const [showResetPwd, setShowResetPwd] = useState(false);
  const [pwdForm, setPwdForm] = useState({ new_password: "", force_password_change: false });
  const [showChangeLogin, setShowChangeLogin] = useState(false);
  const [newLogin, setNewLogin] = useState("");
  const [createdBanner, setCreatedBanner] = useState(null); // {login, password}

  const fetchAccess = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/employees/${employee.id}/access`);
      setAccess(r.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchAccess(); }, [employee.id]);   // eslint-disable-line react-hooks/exhaustive-deps

  const create = async () => {
    if (!createForm.login.trim()) { toast.error("Введіть логін"); return; }
    if (!createForm.password.trim() || createForm.password.length < 4) { toast.error("Пароль ≥ 4 символи"); return; }
    try {
      await api.post(`/employees/${employee.id}/access`, createForm);
      setCreatedBanner({ login: createForm.login.trim(), password: createForm.password });
      setShowCreate(false);
      setCreateForm({ login: "", password: "", role: "doctor", force_password_change: false });
      await fetchAccess(); onChange && onChange();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const doResetPwd = async () => {
    if (!pwdForm.new_password.trim() || pwdForm.new_password.length < 4) { toast.error("Пароль ≥ 4 символи"); return; }
    try {
      await api.post(`/employees/${employee.id}/access/password`, pwdForm);
      setCreatedBanner({ login: access.login, password: pwdForm.new_password });
      setShowResetPwd(false);
      setPwdForm({ new_password: "", force_password_change: false });
      toast.success("Пароль оновлено");
      await fetchAccess();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const doChangeLogin = async () => {
    if (!newLogin.trim()) { toast.error("Введіть логін"); return; }
    try {
      await api.put(`/employees/${employee.id}/access/login`, { login: newLogin.trim() });
      toast.success("Логін оновлено");
      setShowChangeLogin(false); setNewLogin("");
      await fetchAccess();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const doChangeRole = async (newRole) => {
    if (newRole === access.role) return;
    try {
      await api.put(`/employees/${employee.id}/access/role`, { role: newRole });
      toast.success("Роль оновлено");
      await fetchAccess();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const doToggle = async () => {
    const next = access.status !== "active";
    try {
      await api.post(`/employees/${employee.id}/access/toggle`, { is_active: next });
      toast.success(next ? "Доступ активовано" : "Доступ заблоковано");
      await fetchAccess();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (loading) return <div className="mt-4 p-3 text-xs text-slate-400">Завантаження доступу…</div>;

  const statusBadge = access?.status === "active"
    ? <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200">Активний</Badge>
    : access?.status === "blocked"
      ? <Badge className="bg-rose-50 text-rose-700 border border-rose-200">Заблокований</Badge>
      : <Badge className="bg-slate-100 text-slate-500 border border-slate-200">Акаунт не створено</Badge>;

  return (
    <div className="mt-6 p-4 border-2 border-dashed border-[#0D9488]/40 rounded-lg bg-teal-50/30" data-testid="access-panel">
      <div className="flex items-center gap-2 mb-3">
        <ShieldCheck className="h-5 w-5 text-[#0D9488]" />
        <h3 className="font-semibold text-[#0F2027]">Доступ до системи</h3>
        <div className="ml-auto">{statusBadge}</div>
      </div>

      {createdBanner && (
        <div className="mb-3 p-3 bg-amber-50 border border-amber-300 rounded text-sm" data-testid="access-banner">
          <div className="font-semibold text-amber-900 mb-1">Тимчасові дані для передачі працівнику (показуються один раз):</div>
          <div className="font-mono text-xs">
            <div>Логін: <b>{createdBanner.login}</b></div>
            <div>Пароль: <b>{createdBanner.password}</b></div>
          </div>
          <Button size="sm" variant="outline" className="mt-2" onClick={() => setCreatedBanner(null)} data-testid="banner-close">Закрити</Button>
        </div>
      )}

      {access?.status === "no_account" ? (
        <>
          {!showCreate ? (
            <Button size="sm" onClick={() => setShowCreate(true)} className="bg-[#0D9488] hover:bg-[#0D9488]/90" data-testid="access-create">
              <UserIcon className="h-4 w-4 mr-1" /> Створити доступ
            </Button>
          ) : (
            <div className="space-y-2 bg-white p-3 rounded border border-slate-200">
              <div className="text-xs text-slate-500 mb-1">Працівник: <b>{employee.full_name}</b> (заповнено автоматично)</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div><Label className="text-xs">Логін <span className="text-rose-500">*</span></Label>
                  <Input data-testid="access-new-login" value={createForm.login} onChange={(e) => setCreateForm({ ...createForm, login: e.target.value })} placeholder="напр. і.демчук" /></div>
                <div><Label className="text-xs">Тимчасовий пароль <span className="text-rose-500">*</span></Label>
                  <Input data-testid="access-new-password" type="text" value={createForm.password} onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })} placeholder="≥ 4 символи" /></div>
                <div><Label className="text-xs">Системна роль <span className="text-rose-500">*</span></Label>
                  <Select value={createForm.role} onValueChange={(v) => setCreateForm({ ...createForm, role: v })}>
                    <SelectTrigger data-testid="access-new-role"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {SYSTEM_ROLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <label className="flex items-center gap-2 mt-6">
                  <input type="checkbox" checked={createForm.force_password_change} onChange={(e) => setCreateForm({ ...createForm, force_password_change: e.target.checked })} />
                  <span className="text-xs">Змінити пароль при першому вході</span>
                </label>
              </div>
              <div className="flex gap-2 justify-end">
                <Button size="sm" variant="outline" onClick={() => setShowCreate(false)}>Скасувати</Button>
                <Button size="sm" onClick={create} className="bg-[#0D9488] hover:bg-[#0D9488]/90" data-testid="access-create-confirm">Створити</Button>
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
            <div><span className="text-slate-500 text-xs">Логін:</span> <b className="font-mono">{access.login || "—"}</b></div>
            <div><span className="text-slate-500 text-xs">Системна роль:</span> <b>{access.role_label}</b></div>
            <div><span className="text-slate-500 text-xs">Останній вхід:</span> {access.last_login_at ? access.last_login_at.slice(0, 16).replace("T", " ") : "—"}</div>
            <div><span className="text-slate-500 text-xs">Створено:</span> {(access.created_at || "").slice(0, 10)}</div>
          </div>

          {/* Change system role */}
          <div>
            <Label className="text-xs">Змінити системну роль</Label>
            <Select value={access.role} onValueChange={doChangeRole}>
              <SelectTrigger data-testid="access-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                {SYSTEM_ROLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="flex gap-2 flex-wrap">
            {!showChangeLogin && (
              <Button size="sm" variant="outline" onClick={() => { setNewLogin(access.login || ""); setShowChangeLogin(true); }} data-testid="access-change-login">
                <UserIcon className="h-3 w-3 mr-1" /> Змінити логін
              </Button>
            )}
            {!showResetPwd && (
              <Button size="sm" variant="outline" onClick={() => setShowResetPwd(true)} data-testid="access-reset-pwd">
                <Key className="h-3 w-3 mr-1" /> Встановити новий пароль
              </Button>
            )}
            {access.status === "active" ? (
              <Button size="sm" variant="outline" onClick={doToggle} data-testid="access-block" className="text-red-600 border-red-200 hover:bg-red-50">
                <Lock className="h-3 w-3 mr-1" /> Заблокувати доступ
              </Button>
            ) : (
              <Button size="sm" variant="outline" onClick={doToggle} data-testid="access-activate" className="text-emerald-600 border-emerald-200 hover:bg-emerald-50">
                <Unlock className="h-3 w-3 mr-1" /> Активувати доступ
              </Button>
            )}
          </div>

          {showChangeLogin && (
            <div className="bg-white p-3 rounded border border-slate-200 space-y-2">
              <Label className="text-xs">Новий логін</Label>
              <div className="flex gap-2">
                <Input data-testid="change-login-input" value={newLogin} onChange={(e) => setNewLogin(e.target.value)} placeholder="без пробілів" />
                <Button size="sm" onClick={doChangeLogin} data-testid="change-login-save">Зберегти</Button>
                <Button size="sm" variant="ghost" onClick={() => setShowChangeLogin(false)}>Скасувати</Button>
              </div>
            </div>
          )}

          {showResetPwd && (
            <div className="bg-white p-3 rounded border border-slate-200 space-y-2">
              <Label className="text-xs">Новий пароль (≥ 4 символи)</Label>
              <Input data-testid="reset-pwd-input" type="text" value={pwdForm.new_password} onChange={(e) => setPwdForm({ ...pwdForm, new_password: e.target.value })} />
              <label className="flex items-center gap-2 text-xs">
                <input type="checkbox" checked={pwdForm.force_password_change} onChange={(e) => setPwdForm({ ...pwdForm, force_password_change: e.target.checked })} />
                Вимагати зміну пароля при наступному вході
              </label>
              <div className="flex gap-2 justify-end">
                <Button size="sm" variant="outline" onClick={() => setShowResetPwd(false)}>Скасувати</Button>
                <Button size="sm" onClick={doResetPwd} data-testid="reset-pwd-save">Скинути пароль</Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
